/**
 * org.newdawn.slick.Music over HTMLAudioElement.
 *
 * Same surface the game already calls (play/pause/resume/stop/setVolume/getPosition/
 * setPosition/playing/addListener), so AudioPlayer.java ports unchanged.
 *
 * Two browser realities the Java original didn't have to handle:
 *   1. Autoplay is blocked until a user gesture — see `unlock()`, driven by the boot overlay.
 *   2. 24MB of OGG must not all download at once — preload is 'none' until first play.
 */

export interface MusicListener {
  musicEnded(music: Music): void;
  musicSwapped(oldMusic: Music, newMusic: Music): void;
}

const MUSIC_DIR = 'assets/music/';

/** Set true once a user gesture has happened; play() before that is a no-op that arms a retry. */
let audioUnlocked = false;
const pendingOnUnlock: Array<() => void> = [];

export function unlock(): void {
  if (audioUnlocked) return;
  audioUnlocked = true;
  const queued = pendingOnUnlock.splice(0, pendingOnUnlock.length);
  for (const fn of queued) {
    try { fn(); } catch { /* ignore */ }
  }
}

export function isUnlocked(): boolean {
  return audioUnlocked;
}

export class Music {
  private readonly el: HTMLAudioElement;
  private readonly listeners: MusicListener[] = [];
  private volume = 1;
  /** True between play() and stop(); mirrors Slick's notion of "this track is the active one". */
  private active = false;

  constructor(readonly path: string) {
    this.el = new Audio();
    // Filenames contain spaces and parentheses; encode each path segment.
    this.el.src = MUSIC_DIR + encodeURIComponent(path);
    this.el.preload = 'none';
    this.el.loop = false;
    this.el.volume = this.volume;
    this.el.addEventListener('ended', () => {
      this.active = false;
      for (const l of this.listeners) l.musicEnded(this);
    });
  }

  addListener(l: MusicListener): void {
    this.listeners.push(l);
  }

  removeListener(l: MusicListener): void {
    const i = this.listeners.indexOf(l);
    if (i >= 0) this.listeners.splice(i, 1);
  }

  play(): void {
    this.active = true;
    this.rewind();
    this.startPlayback();
  }

  /**
   * Seek back to 0, but only when there is actually something to seek.
   *
   * With preload='none' the element has no data yet and currentTime is already 0. Assigning
   * to it anyway kicks off a fetch that the subsequent play() immediately supersedes, which
   * the browser reports as a failed request (ERR_ABORTED) and which can drop the track.
   */
  private rewind(): void {
    if (this.el.readyState === 0 || this.el.currentTime === 0) return;
    try {
      this.el.currentTime = 0;
    } catch {
      // Not seekable yet — harmless, playback still starts from the beginning.
    }
  }

  resume(): void {
    this.active = true;
    this.startPlayback();
  }

  private startPlayback(): void {
    const attempt = () => {
      // preload='none' means the element may have no data yet; play() triggers the fetch.
      const p = this.el.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // Most commonly NotAllowedError (no gesture yet). Retry once unlocked.
          if (!audioUnlocked) pendingOnUnlock.push(attempt);
        });
      }
    };
    if (!audioUnlocked) {
      pendingOnUnlock.push(attempt);
      return;
    }
    attempt();
  }

  pause(): void {
    this.el.pause();
  }

  stop(): void {
    this.active = false;
    this.el.pause();
    this.rewind();
  }

  playing(): boolean {
    return this.active && !this.el.paused && !this.el.ended;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.el.volume = this.volume;
  }

  getVolume(): number {
    return this.volume;
  }

  /** Position in seconds. */
  getPosition(): number {
    return this.el.currentTime || 0;
  }

  setPosition(seconds: number): boolean {
    try {
      this.el.currentTime = Math.max(0, seconds);
      return true;
    } catch {
      return false;
    }
  }

  /** Duration in seconds, or 0 while metadata is still loading. */
  getDuration(): number {
    return Number.isFinite(this.el.duration) ? this.el.duration : 0;
  }
}

/** org.newdawn.slick.Sound — the original build ships no SFX; kept so AudioPlayer compiles. */
export class Sound {
  constructor(readonly path: string) {}
  play(_pitch?: number, _volume?: number): void { /* no SFX in this build */ }
  stop(): void { /* no-op */ }
}
